import fetchStorefrontApi from './api';
import generateModalTemplate from './generateModalTemplate';

export default class GeolocationModal {
  constructor() {
    this.modalNamespace = 'geolocation-modal';
    this.modalEl = document.getElementById(this.modalNamespace);

    if (this.modalEl && !window.Shopify.designMode) {
      // Use the same cookie name as from the deprecated geolocation app
      this.cookieName = 'geolocation_modal_accepted';
      this.cookieValue = '1';
      this.defaultLanguage = 'EN';

      // Use cookies instead of localeStorage, check if the cookie is set and if it's 1
      const cookieRow = document.cookie.split('; ').find(row => row.startsWith(this.cookieName));
      const currentCookieValue = cookieRow ? cookieRow.split('=')[1].toString() : null;

      const { key } = this.modalEl.dataset;
      this.storefrontApiAccessToken = key;

      if (!currentCookieValue || currentCookieValue !== this.cookieValue) {
        this.getModal();
      }
    }
  }

  setGeolocationCookie() {
    document.cookie = `${this.cookieName}=${this.cookieValue}; path=/; expires=Fri, 31 Dec 9999 23:59:59 GMT;`;
  }

  async fetchGeolocationSuggestions() {
    try {
      const suggestionsResponse = await fetch(
        `${window.Shopify.routes.root}browsing_context_suggestions.json` +
          `?country[enabled]=true` +
          `&currency[enabled]=true` +
          `&language[enabled]=true`,
      ).then(r => r.json());

      let recommendedLanguage = null;
      let recommendedLanguageCode = null;
      let recommendedCountry = null;
      let recommendedCountryCode = null;
      let recommendedCurrency = null;
      let recommendedCurrencyName = null;

      if (suggestionsResponse.suggestions.length > 0) {
        const suggestions = suggestionsResponse.suggestions[0];
        const {
          country: countryParts,
          language: languageParts,
          currency: currencyParts,
        } = suggestions.parts;

        if (languageParts) {
          recommendedLanguage = languageParts.name;
          recommendedLanguageCode = languageParts.handle;
        }

        if (countryParts) {
          recommendedCountry = countryParts.name;
          recommendedCountryCode = countryParts.handle;
        }

        if (currencyParts) {
          recommendedCurrency = currencyParts.handle;
          recommendedCurrencyName = currencyParts.name;
        }
      }

      return {
        recommendedLanguage,
        recommendedLanguageCode,
        recommendedCountry,
        recommendedCountryCode,
        recommendedCurrency,
        recommendedCurrencyName,
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching geolocation suggestions:', error);
      return null;
    }
  }

  async fetchLocalization() {
    // When the suggestion is not available, use the default language
    const defaultLanguageCode = this.defaultLanguage;
    const languageCode = this.suggestions.recommendedLanguageCode || defaultLanguageCode;

    const LOCALIZATION_QUERY = `
      query AllLocalizations @inContext(language: ${languageCode.toUpperCase()}) {
        localization {
          availableCountries {
            isoCode
            name
            currency {
              isoCode
            }
            availableLanguages {
              isoCode
              endonymName
            }
          }
        }
      }
    `;

    const response = await fetchStorefrontApi(this.storefrontApiAccessToken, LOCALIZATION_QUERY);
    if (!response) {
      return null;
    }

    return response.localization;
  }

  async getModal() {
    // Await fetching browsing context suggestions
    this.suggestions = await this.fetchGeolocationSuggestions();

    // Check if the current market is different from the recommended market
    // Use the Shopify.locale and Shopify.country to check if the current market is different from the recommended market
    let isCurrentMarketDifferentFromRecommendedMarket = true;
    if (this.suggestions.recommendedLanguageCode && this.suggestions.recommendedCountryCode) {
      isCurrentMarketDifferentFromRecommendedMarket =
        window.Shopify.locale !== this.suggestions.recommendedLanguageCode ||
        window.Shopify.country !== this.suggestions.recommendedCountryCode;
    }

    // Await fetching markets
    if (isCurrentMarketDifferentFromRecommendedMarket) {
      this.localization = await this.fetchLocalization();
    }

    // When the suggested language is null & there's a suggested country,
    // check if the suggested country has only one language
    // and put it as the suggested language
    if (!this.suggestions.recommendedLanguageCode && this.suggestions.recommendedCountryCode) {
      // Selected the country by the recommended country code
      const selectedCountry = this.localization.availableCountries.find(
        country => country.isoCode === this.suggestions.recommendedCountryCode,
      );

      // Check if the selected country has only one language
      const isOnlyOneLanguage = selectedCountry.availableLanguages.length === 1;

      if (isOnlyOneLanguage) {
        const selectedLanguage = selectedCountry.availableLanguages[0];
        this.suggestions.recommendedLanguageCode = selectedLanguage.isoCode.toLowerCase();
        this.suggestions.recommendedLanguage = selectedLanguage.endonymName;

        // Redo the localization fetch
        this.localization = await this.fetchLocalization();

        // Set country name
        const selectedCountryLocalization = this.localization.availableCountries.find(
          country => country.isoCode === this.suggestions.recommendedCountryCode,
        );
        this.suggestions.recommendedCountry = selectedCountryLocalization.name;
        this.suggestions.recommendedCountryCode = selectedCountryLocalization.isoCode;

        // Redo the current market check
        isCurrentMarketDifferentFromRecommendedMarket =
          window.Shopify.locale !== this.suggestions.recommendedLanguageCode ||
          window.Shopify.country !== this.suggestions.recommendedCountryCode;
      }
    }

    // If the current market is the same as the recommended market, set the cookie to 1
    if (!isCurrentMarketDifferentFromRecommendedMarket) {
      this.setGeolocationCookie();
    }

    // If there's localization and the current market is different from the recommended market, render the modal
    if (this.localization && isCurrentMarketDifferentFromRecommendedMarket) {
      const modalTemplate = generateModalTemplate(this.suggestions, this.localization);
      const modalContainerEl = document.getElementById('geolocation-container');
      if (modalContainerEl) {
        modalContainerEl.innerHTML = modalTemplate;
      }

      this.handleFormEvents();

      const modal = window.modalManager.idModalMap.get(this.modalNamespace);
      if (modal) {
        modal.show();
      }

      // set local storage to prevent showing the modal again
      this.modalEl.querySelectorAll('.js-modal-close').forEach(el => {
        el.addEventListener('click', () => {
          modal.hide();
          this.setGeolocationCookie();
        });
      });

      this.modalEl.querySelectorAll('.js-geolocation-submit').forEach(el => {
        el.addEventListener('click', () => {
          this.setGeolocationCookie();
        });
      });

      // close modal cookie
      modal.on('before-hide', () => {
        this.setGeolocationCookie();
      });
    }
  }

  handleFormEvents() {
    this.countrySelect = this.modalEl.querySelector('select[name="country_code"]');
    this.languageSelect = this.modalEl.querySelector('select[name="language_code"]');
    this.recommendedModal = this.modalEl.querySelector('.js-geolocation-recommended');
    this.selectMarketModal = this.modalEl.querySelector('.js-geolocation-select');

    this.changeButton = this.modalEl.querySelector('.js-geolocation-change');
    this.backButton = this.modalEl.querySelector('.js-geolocation-back');

    if (this.countrySelect) {
      this.countrySelect.addEventListener('change', this.handleCountrySelectChange.bind(this));
    }

    if (this.changeButton) {
      this.changeButton.addEventListener('click', this.handleViewSelectMarket.bind(this));
    }

    if (this.backButton) {
      this.backButton.addEventListener('click', this.handleViewRecommended.bind(this));
    }

    this.renderLanguageOptions(
      this.suggestions.recommendedCountryCode,
      this.suggestions.recommendedLanguageCode,
    );
  }

  handleCountrySelectChange() {
    const selectedCountry = this.countrySelect.value;
    this.renderLanguageOptions(selectedCountry, this.suggestions.recommendedLanguageCode);
  }

  renderLanguageOptions(countryCode, preselectedLanguageCodeSuggestion) {
    // If there's no localization, don't render the language options
    if (!this.localization.availableCountries.length) {
      return;
    }

    const preselectedLanguageCode = preselectedLanguageCodeSuggestion || this.defaultLanguage;
    let selectedCountry = this.localization.availableCountries.find(
      country => country.isoCode === countryCode,
    );

    if (!selectedCountry) {
      [selectedCountry] = this.localization.availableCountries;
    }

    // Try to find the preselected language first
    let preselectedLanguage = null;
    if (selectedCountry) {
      preselectedLanguage = selectedCountry.availableLanguages.find(
        language => language.isoCode === preselectedLanguageCode.toUpperCase(),
      );
    }

    let selectedLanguageCode = preselectedLanguage ? preselectedLanguage.isoCode : null;

    // If preselected language not available, try the language of the current html page
    if (!selectedLanguageCode && selectedCountry) {
      const currentLanguageCode = window.Shopify.locale;
      const currentLanguage = selectedCountry.availableLanguages.find(
        language => language.isoCode === currentLanguageCode.toUpperCase(),
      );
      selectedLanguageCode = currentLanguage ? currentLanguage.isoCode : null;
    }

    // If neither language is available, use the first available language
    if (!selectedLanguageCode && selectedCountry && selectedCountry.availableLanguages.length > 0) {
      selectedLanguageCode = selectedCountry.availableLanguages[0].isoCode;
    }

    const languageOptions = selectedCountry.availableLanguages.map(language => {
      const isSelected =
        selectedLanguageCode && language.isoCode === selectedLanguageCode.toUpperCase();
      return `<option value="${language.isoCode}" ${isSelected ? 'selected' : ''}>${
        language.endonymName
      }</option>`;
    });

    this.languageSelect.innerHTML = languageOptions.join('');
  }

  handleViewSelectMarket() {
    this.recommendedModal.style.display = 'none';
    this.selectMarketModal.style.display = 'block';
  }

  handleViewRecommended() {
    this.recommendedModal.style.display = 'block';
    this.selectMarketModal.style.display = 'none';
  }
}
